import clsx from 'clsx';
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import Link from '@dashboard/components/Link';
import LogoLockup from '@dashboard/components/LogoLockup';
import { tracking, useTracking } from '@dashboard/lib/tracking';
import insertScript from '@dashboard/lib/insertScript';
import { isProduction } from '@dashboard/services/env';
import { injectTrackingScript } from '@/dashboard/lib/scriptInjector';

// The marketing site's Container (apps/site/components/container.js), so the auth pages line up with it.
const siteContainer = 'mx-auto w-full max-w-7xl px-4 2xl:px-0';

const AuthLayout = ({ children, legalLinks = true }) => {
  useTracking();
  const location = useLocation();

  // track pageviews
  useEffect(() => {
    tracking.trackPageview();
  }, [location]);

  // load google ads
  useEffect(() => {
    function loadGoogleAds() {
      injectTrackingScript(
        `
        <!-- Google tag (gtag.js) -->
        <script async src="https://www.googletagmanager.com/gtag/js?id=G-ZQ3SPC315S"></script>
        <script>
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());

          gtag('config', 'G-ZQ3SPC315S');
          gtag('config', 'AW-16833295745');
        </script>
      `,
      );
    }

    if (isProduction) {
      loadGoogleAds();
    }
  }, []);

  // load facebook pixel
  useEffect(() => {
    function loadFacebookPixel() {
      insertScript(`
        <!-- Meta Pixel Code -->
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '976736210989373');
        fbq('track', 'PageView');
        <!-- End Meta Pixel Code -->
      `);
    }

    if (isProduction) {
      loadFacebookPixel();
    }
  }, []);

  useEffect(() => {
    function loadLinkedInInsight() {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.innerHTML = `
        _linkedin_partner_id = "7177180";
        window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
        window._linkedin_data_partner_ids.push(_linkedin_partner_id);

        (function(l) {
        if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
        window.lintrk.q=[]}
        var s = document.getElementsByTagName("script")[0];
        var b = document.createElement("script");
        b.type = "text/javascript";b.async = true;
        b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
        s.parentNode.insertBefore(b, s);})(window.lintrk);
      `;
      document.body.appendChild(script);

      const noscript = document.createElement('noscript');
      noscript.innerHTML = `
        <img
          height="1"
          width="1"
          style="display:none;"
          alt=""
          src="https://px.ads.linkedin.com/collect/?pid=7177180&fmt=gif"
        />
      `;
      document.body.appendChild(noscript);
    }

    if (isProduction) {
      loadLinkedInInsight();
    }
  }, []);

  return (
    <div className="relative flex min-h-[100dvh] flex-col bg-white dark:bg-background">
      <header className="relative border-b border-gray-100 dark:border-border bg-white dark:bg-background">
        <div className={clsx(siteContainer, 'flex h-16 items-center lg:h-14')}>
          <Link
            href="/"
            bare
            className="inline-flex items-center text-black dark:text-foreground no-underline hover:text-black dark:hover:text-foreground"
          >
            <LogoLockup height={20} />
            <span className="sr-only">Checkout Page</span>
          </Link>
        </div>
      </header>

      <main className="relative flex flex-1">
        <div
          className={clsx(
            siteContainer,
            'relative flex justify-center pb-10 pt-8 sm:pb-16 sm:pt-[12vh]',
          )}
        >
          <div className="w-full max-w-[400px] motion-safe:animate-slide-up">{children}</div>
        </div>
      </main>

      <footer className="relative">
        <div className={siteContainer}>
          <div className="flex flex-col items-center gap-y-2 py-6 text-center text-xs text-gray-600 dark:text-muted-foreground sm:flex-row sm:justify-between sm:py-8">
            <p>© {new Date().getFullYear()} Checkout Page. All rights reserved.</p>
            {legalLinks && (
              <div className="space-x-4">
                <a
                  className="hover:text-gray-900 dark:hover:text-foreground hover:underline"
                  href="/terms-of-service"
                  target="_blank"
                >
                  Terms of Service
                </a>
                <a
                  className="hover:text-gray-900 dark:hover:text-foreground hover:underline"
                  href="/privacy-policy"
                  target="_blank"
                >
                  Privacy policy
                </a>
              </div>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AuthLayout;
